import React, { useMemo } from 'react';
import { PresentationEditorHeader } from '../editor-page-header';
import { PreviewLayoutMode } from '../../entities/preview-layout-mode';
import { BwjPageContent } from '../../../../framework/components/bwj-page-content';
import { ContainerDisposition } from '../../../../components/dynamic-duo-container/disposition';
import { DynamicDuoContainer } from '../../../../components/dynamic-duo-container';
import { IPresentationEditorFormModel } from '../../form-services/presentation-editor-iface';
import * as formService from '../../form-services/presentation-editor';
import * as preferencesService from '../../../presenter-account/form-services/account-preferences';
import { xlate } from '../../../../framework/utils/i18n';
import { BwjTextField } from '../../../../framework/components/bwj-text-field';
import { BwjInput } from '../../../../framework/entities/bwj-input';
import { MarkdownViewport } from '../../../markdown/components/viewport';
import { ITimingSpecComponentModel } from '../../../markdown/parser/timing-spec';
import { BwjScrollContainer } from '../../../../framework/components/bwj-scroll-container';
import { BwjSemiFlexContainer } from '../../../../framework/components/bwj-semi-flex-container';
import { getTheme } from '@fluentui/react';
import { MarkdownHelpViewer } from '../../../markdown/components/help-viewer';
import { PresentationEditorPreviewHelpPanel } from '../editor-preview-help-panel';
import { IShowAnnouncementBannerArgs } from '../../../../framework/components/bwj-announcement/props';
import { BwjAnnouncementBanner } from '../../../../framework/components/bwj-announcement';

interface IUIProps {
  onSavePresentation: () => void;
  onStartPresentation: (() => void) | undefined;
  onDisplayPreview: () => void;
  onDisplayHelp: () => void;
  onOpenPreviewPanel: () => void;
  onClosePreviewPanel: () => void;
  showingPreviewHelpPanel: boolean;
  showHelpContent: boolean;
  saveDisabled: boolean;
  previewLayout: PreviewLayoutMode;
  userLayoutMode: PreviewLayoutMode | undefined;
  formModel: IPresentationEditorFormModel;
  onContainerSizeChange: (width: number, height: number) => void;
  onSetUserLayoutMode: (mode: PreviewLayoutMode | undefined) => void;
  announcementArgs: IShowAnnouncementBannerArgs | undefined;
}

const layoutModeToContentDisposition = (layout: PreviewLayoutMode) => {
  switch(layout) {
    case PreviewLayoutMode.Horizontal: return ContainerDisposition.DualHorizontal;
    case PreviewLayoutMode.Vertical: return ContainerDisposition.DualVertical;
    default: return ContainerDisposition.Single;
  }
}

const editorInputStyles = {
  root: {
    width: '100%',
    height: '100%',
    '& div': {
      height: '100%',
    },
    '& textarea': {
      overflowY: 'auto',
    }
  },
  fieldGroup: {
    border: 'none',
    '&::after': {
      outline: 0,
      border: 0,
      inset: 0,
    },
  },
  field: {
    resize: 'none',
    height: '100% !important',
  },
};

const subscriberId = 'Component.PresentationEditor.UI';

export const UI: React.FunctionComponent<IUIProps> = (props) => {
  const theme = getTheme();
  const { formModel, showHelpContent } = props;
  const prefsFormModel = preferencesService.useFormModel(subscriberId);
  const fontSizePreference = useMemo(() => {
    return preferencesService.toRequest().fontSizePreference;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefsFormModel]);

  const allowStartPresentationButton = !!props.onStartPresentation;

  const markdownEditor = useMemo(() => {
    return (
      <BwjTextField
        id={formService.content.id}
        required={false}
        onUpdate={formService.content.onUpdate}
        input={new BwjInput<string>(formModel.content)}
        filter={/.{0,64000}/}
        placeholder={xlate('Start typing your discussion plan here')}
        multiline={true}
        updateOnAnyChange={true}
        styles={editorInputStyles}
      />
    );
  }, [formModel]);

  const previewOrHelpDisplay = useMemo(() => {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <BwjSemiFlexContainer
          pinHeight={true}
          styles={{
            root: {
              flexGrow: 1,
            }
          }}
        >
          {showHelpContent ?
          <BwjScrollContainer>
            <MarkdownHelpViewer />
          </BwjScrollContainer>
          :
          <BwjScrollContainer>
            <MarkdownViewport
              componentModel={formModel.componentModel as ITimingSpecComponentModel[]}
              baseFontSize={fontSizePreference}
            />
          </BwjScrollContainer>}
        </BwjSemiFlexContainer>
      </div>
    );
  }, [formModel, showHelpContent, fontSizePreference]);

  return (
      <>
      <PresentationEditorPreviewHelpPanel
        showPanel={props.showingPreviewHelpPanel}
        showHelp={props.showHelpContent}
        previewModel={formModel.componentModel as ITimingSpecComponentModel[]}
        previewBaseFontSize={fontSizePreference}
        onClosePanel={props.onClosePreviewPanel}
      />
      <BwjAnnouncementBanner
        invokeShowAnnouncement={props.announcementArgs}
        closeDialogButtonText={xlate('Close dialog')}
        ariaShowFullMessageDialog={xlate('Show full message')}
      />
      <PresentationEditorHeader
        onSavePresentation={props.onSavePresentation}
        onStartPresentation={props.onStartPresentation}
        onDisplayHelp={props.onDisplayHelp}
        onDisplayPreview={props.onDisplayPreview}
        saveDisabled={props.saveDisabled}
        onOpenPreviewPanel={props.onOpenPreviewPanel}
        allowStartPresentationButton={allowStartPresentationButton}
        showDisplayPreviewHelpPanelButton={props.showHelpContent}
        showDisplayPanelButton={props.previewLayout === PreviewLayoutMode.Paneled}
        userLayoutMode={props.userLayoutMode}
        onSetUserLayoutMode={props.onSetUserLayoutMode}
      />
      <BwjPageContent
        contentDescription='primary content (no subcontent)'
        style={{ padding: 0, backgroundColor: theme.palette.neutralLight }}
      >
        <DynamicDuoContainer
          topLeftContent={markdownEditor}
          bottomRightContent={previewOrHelpDisplay}
          disposition={layoutModeToContentDisposition(props.previewLayout)}
          onContainerSizeChange={props.onContainerSizeChange}
        />
      </BwjPageContent>
    </>
  );
};
